import React, {
  useContext,
  useMemo,
  useCallback,
  useRef,
  useState,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { I18nContext } from '@bingads-webui-react/i18n-context';
import { TextField } from '@bingads-webui-react/fabric-control';

import { FluentImagePicker } from '@bingads-webui-campaign-react/fluent-image-picker';
import { AssetLibrarySpinner } from '@bingads-webui-campaign-react/asset-library-spinner';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getUrlProtocolAndDomain, hasProtocol, isValidUrl } from '@bingads-webui/url-util';

import {
  getWebsiteImagesStyles,
  websiteImagesFinalUrlTextFieldStyles,
  websiteImagesFinalUrlTextFieldIconProps,
  websiteImagesSubdomainTextFieldStyles,
} from './styles/index';
import { PlaceholderView } from './placeholder-view';

export const WebsiteImageView = ({
  images,
  isLoadingFirstPage,
  isLoading,
  hasMore,
  finalUrl,
  subdomain,
  loadNextPage,
  onSelectImage,
  onEnteredFinalUrl,
  onSubdomainChange,
  onSubdomainChanged,
  pageSize,
  protocol,
  onUpdateProtocol,
  showTextAbovePicker,
  imageCellWidth,
  imageCellHeight,
  enableEditFinalUrlInListMode,
}) => {
  const showPlaceholder =
    useMemo(() => !finalUrl || !isValidUrl(finalUrl), [finalUrl]);

  const finalUrlDomain = useMemo(() => {
    if (finalUrl) {
      return getUrlProtocolAndDomain(finalUrl);
    }
    return '';
  }, [finalUrl]);

  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getWebsiteImagesStyles);

  const [temporaryFinalUrl, setTemporaryFinalUrl] = useState(finalUrlDomain);

  useEffect(() => {
    if (finalUrlDomain !== temporaryFinalUrl) {
      setTemporaryFinalUrl(finalUrlDomain);
    }
  }, [finalUrlDomain, finalUrl]);

  const subdomainRef = useRef();
  const finalUrlRef = useRef();

  const onKeyDownSubdomain = useCallback((ev) => {
    if (ev.key === 'Enter') {
      onSubdomainChanged(ev);
      if (subdomainRef.current) {
        subdomainRef.current.blur();
      }
    }
  }, [onSubdomainChanged]);

  const onKeyDownFinalUrl = useCallback((ev) => {
    if (ev.key === 'Enter') {
      let updatedFinalUrl = getUrlProtocolAndDomain(temporaryFinalUrl);

      if (!hasProtocol(updatedFinalUrl)) {
        updatedFinalUrl = `https://${updatedFinalUrl}`;
      }

      onEnteredFinalUrl(updatedFinalUrl, ev);
      if (finalUrlRef.current) {
        finalUrlRef.current.blur();
      }
    }
  }, [onEnteredFinalUrl, temporaryFinalUrl]);

  const onFinalUrlFieldChange = useCallback((ev, newValue) => {
    setTemporaryFinalUrl(newValue);
  }, [setTemporaryFinalUrl]);

  const onFinalUrlBlur = useCallback((ev) => {
    let updatedFinalUrl = getUrlProtocolAndDomain(temporaryFinalUrl);

    if (!hasProtocol(updatedFinalUrl)) {
      updatedFinalUrl = `https://${updatedFinalUrl}`;
    }

    onEnteredFinalUrl(updatedFinalUrl, ev);
  }, [temporaryFinalUrl, onEnteredFinalUrl]);

  return (
    <div className={classnames('website-image-view', classes.rootContainer)}>
      {
        showPlaceholder ?
          <PlaceholderView
            onEnteredFinalUrl={onEnteredFinalUrl}
            protocol={protocol}
            onUpdateProtocol={onUpdateProtocol}
            initialFinalUrl={finalUrl}
          /> : (
            <div className={classnames('subdomain-fields', classes.subdomainFields)}>
              {showTextAbovePicker && (
                <div className={classnames(classes.textAbovePicker)}>
                  {i18n.getString(_TL_('We found images from your Final URL:'))}
                </div>
              )}
              <TextField
                disabled={!enableEditFinalUrlInListMode || isLoading}
                className="final-url"
                value={temporaryFinalUrl}
                ariaLabel={i18n.getString(_TL_('Final url full domain'))}
                styles={websiteImagesFinalUrlTextFieldStyles}
                iconProps={websiteImagesFinalUrlTextFieldIconProps}
                onKeyDown={onKeyDownFinalUrl}
                onBlur={onFinalUrlBlur}
                onChange={onFinalUrlFieldChange}
                componentRef={finalUrlRef}
              />
              <div className="divider">
                {'/'}
              </div>
              <TextField
                disabled={isLoading}
                className="subdomain"
                placeholder={i18n.getString(_TL_('Enter your subdomain'))}
                onChange={onSubdomainChange}
                onBlur={onSubdomainChanged}
                value={subdomain}
                styles={websiteImagesSubdomainTextFieldStyles}
                onKeyDown={onKeyDownSubdomain}
                componentRef={subdomainRef}
              />
            </div>
        )
      }
      {
        !showPlaceholder && !isLoading && images.length === 0 &&
        <div className="alert alert-info" style={{ margin: '3px 0' }}>
          <div className="alert-image" />
          <div className="alert-info-inline">
            {i18n.getString(_TL_('No suggestion found from your site.'))}
          </div>
        </div>
      }
      {
        !showPlaceholder && (
          isLoadingFirstPage ? <AssetLibrarySpinner useCustomSize customStyle={{ marginTop: '50px' }} /> :
          <FluentImagePicker
            images={images}
            pageSize={pageSize}
            loadMoreImages={loadNextPage}
            onSelectImage={onSelectImage}
            hasMoreImages={hasMore}
            imageCellWidth={imageCellWidth}
            imageCellHeight={imageCellHeight}
          />
        )
      }
    </div>
  );
};

WebsiteImageView.propTypes = {
  images: PropTypes.arrayOf(PropTypes.shape({
    thumbnailSrc: PropTypes.string,
  })).isRequired,
  isLoadingFirstPage: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  hasMore: PropTypes.bool.isRequired,
  loadNextPage: PropTypes.func.isRequired,
  onSelectImage: PropTypes.func.isRequired,
  onEnteredFinalUrl: PropTypes.func.isRequired,
  onSubdomainChange: PropTypes.func.isRequired,
  onSubdomainChanged: PropTypes.func.isRequired,
  pageSize: PropTypes.number,
  finalUrl: PropTypes.string,
  subdomain: PropTypes.string,
  protocol: PropTypes.string,
  onUpdateProtocol: PropTypes.func,
  showTextAbovePicker: PropTypes.bool,
  imageCellWidth: PropTypes.number,
  imageCellHeight: PropTypes.number,
  enableEditFinalUrlInListMode: PropTypes.bool,
};

WebsiteImageView.defaultProps = {
  finalUrl: '',
  subdomain: '',
  pageSize: 30,
  protocol: null,
  onUpdateProtocol: null,
  showTextAbovePicker: false,
  imageCellWidth: undefined,
  imageCellHeight: undefined,
  enableEditFinalUrlInListMode: false,
};
